import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Lock, Unlock, Check, Loader2, RotateCcw,
  AlertTriangle, XCircle, CheckCircle, Target, Power, Zap,
  Eye, Edit3, TestTube, Hammer, Compass, Settings2
} from 'lucide-react';
import {
  createOrchestratorAPI,
  type OrchestratorState,
  type FrozenIntent,
  type OrchestratorAPI,
  type JudgmentVerdict,
} from '../../lib/orchestrator-api';
import './OrchestratorPanel.css';

const POLL_INTERVAL = 10000; // 10s - reduced from 2s to prevent API hammering

interface OrchestratorPanelProps {
  sessionId: string;
  visible?: boolean;
}

// Judgment verdicts for control buttons
const JUDGMENT_VERDICTS: JudgmentVerdict[] = ['PASS', 'FAIL', 'PENDING'];

// Mode definitions (replaces phase badges)
const MODES = [
  { key: 'NORMAL', label: 'NORMAL', color: 'gray' },
  { key: 'INTERROGATION', label: 'INTERROG', color: 'amber' },
  { key: 'ANALYSIS', label: 'ANALYSIS', color: 'blue' },
  { key: 'EXECUTION', label: 'EXECUTE', color: 'green' },
] as const;

// Get mode color classes
const getModeColorClasses = (mode: string, isActive: boolean): string => {
  if (!isActive) return 'bg-gray-800/30 text-gray-500 border-gray-700/30';

  switch (mode) {
    case 'NORMAL':
      return 'bg-gray-600/30 text-gray-300 border-gray-500/50';
    case 'INTERROGATION':
      return 'bg-amber-600/30 text-amber-300 border-amber-500/50';
    case 'ANALYSIS':
      return 'bg-blue-600/30 text-blue-300 border-blue-500/50';
    case 'EXECUTION':
      return 'bg-green-600/30 text-green-300 border-green-500/50';
    default:
      return 'bg-gray-800/30 text-gray-500 border-gray-700/30';
  }
};

const OrchestratorPanel: React.FC<OrchestratorPanelProps> = ({ sessionId, visible = true }) => {
  const [state, setState] = useState<OrchestratorState | null>(null);
  const [frozenIntent, setFrozenIntent] = useState<FrozenIntent | null>(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // PZRP Scopes state
  interface PzrpScope {
    name: string;
    description: string;
    active: boolean;
    dependencies: string[];
  }
  const [pzrpScopes, setPzrpScopes] = useState<PzrpScope[]>([]);
  const [scopesLoading, setScopesLoading] = useState(false);

  // Control states
  const [orchestrationEnabled, setOrchestrationEnabled] = useState(true);
  const [judgmentGateEnabled, setJudgmentGateEnabled] = useState(false);
  const [judgmentGateLoading, setJudgmentGateLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmingIntent, setConfirmingIntent] = useState(false);
  const [confirmingAcceptance, setConfirmingAcceptance] = useState(false);
  const [preparingExecution, setPreparingExecution] = useState(false);

  // Create API instance for this session - memoized to prevent recreation
  const apiRef = React.useRef<OrchestratorAPI | null>(null);
  if (!apiRef.current || apiRef.current.getSessionId() !== sessionId) {
    apiRef.current = createOrchestratorAPI(sessionId);
  }
  const api = apiRef.current;

  // Fetch PZRP scopes
  const fetchPzrpScopes = useCallback(async () => {
    try {
      const response = await fetch(`/api/claude-logs/api/pzrp/scopes/${sessionId}`);
      const data = await response.json();
      setPzrpScopes(data.scopes || []);
    } catch {
      // Ignore errors
    }
  }, [sessionId]);

  // Toggle a PZRP scope
  const handleToggleScope = async (scopeName: string, enabled: boolean) => {
    if (scopesLoading) return;
    setScopesLoading(true);
    try {
      await fetch(`/api/claude-logs/api/pzrp/scopes/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: scopeName, enabled }),
      });
      await fetchPzrpScopes();
    } catch {
      // Ignore errors
    }
    setScopesLoading(false);
  };

  // Grant all scopes
  const handleGrantAll = async () => {
    if (scopesLoading) return;
    setScopesLoading(true);
    try {
      await fetch(`/api/claude-logs/api/pzrp/scopes/${sessionId}/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scopes: ['READ_PZRP', 'EDIT_PZRP', 'BUILD_PZRP', 'TEST_PZRP', 'EXPLORE_PZRP'] }),
      });
      await fetchPzrpScopes();
    } catch {
      // Ignore errors
    }
    setScopesLoading(false);
  };

  // Clear all scopes
  const handleClearScopes = async () => {
    if (scopesLoading) return;
    setScopesLoading(true);
    try {
      await fetch(`/api/claude-logs/api/pzrp/scopes/${sessionId}`, { method: 'DELETE' });
      await fetchPzrpScopes();
    } catch {
      // Ignore errors
    }
    setScopesLoading(false);
  };

  // Fetch all orchestrator data including judgment gate
  const fetchData = useCallback(async () => {
    try {
      const [stateRes, intentRes, health] = await Promise.all([
        api.getState(),
        api.getFrozenIntent(),
        api.health(),
      ]);
      setState(stateRes);
      setFrozenIntent(intentRes);
      setConnected(health);
      // Sync judgment gate state from orchestrator state
      if (stateRes) {
        setJudgmentGateEnabled(stateRes.judgment_enabled);
      }
      // Fetch PZRP scopes
      await fetchPzrpScopes();
    } catch {
      setConnected(false);
    }
    setLoading(false);
  }, [api, fetchPzrpScopes]);

  // Poll orchestrator - only when visible
  useEffect(() => {
    if (!visible) return;
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData, visible]);

  // Reset handler
  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    await api.reset();
    await fetchData();
    setResetting(false);
  };

  // Confirm execution handler
  const handleConfirmExecution = async () => {
    if (confirming || !state) return;
    setConfirming(true);
    await api.confirmExecution();
    await fetchData();
    setConfirming(false);
  };

  // Toggle orchestration (placeholder - would need backend support)
  const handleToggleOrchestration = () => {
    setOrchestrationEnabled(!orchestrationEnabled);
  };

  // Toggle judgment gate - calls orchestrator API
  const handleToggleJudgmentGate = async () => {
    if (judgmentGateLoading) return;
    setJudgmentGateLoading(true);
    try {
      const newState = !judgmentGateEnabled;
      const result = await api.setJudgmentEnabled(newState);
      if (result) {
        setJudgmentGateEnabled(result.enabled);
      }
      await fetchData(); // Refresh full state
    } catch {
      // Ignore errors
    }
    setJudgmentGateLoading(false);
  };

  // Set judgment verdict - calls orchestrator API
  const handleSetVerdict = async (verdict: JudgmentVerdict) => {
    if (judgmentGateLoading) return;
    setJudgmentGateLoading(true);
    try {
      await api.setJudgmentVerdict(verdict);
      await fetchData(); // Refresh full state
    } catch {
      // Ignore errors
    }
    setJudgmentGateLoading(false);
  };

  // Confirm intent handler
  const handleConfirmIntent = async () => {
    if (confirmingIntent || !state) return;
    setConfirmingIntent(true);
    await api.setFrozenIntent(true);
    await fetchData();
    setConfirmingIntent(false);
  };

  // Revise intent handler (clears frozen intent)
  const handleReviseIntent = async () => {
    if (confirmingIntent || !state) return;
    setConfirmingIntent(true);
    await api.setFrozenIntent(false);
    await fetchData();
    setConfirmingIntent(false);
  };

  // Confirm acceptance handler
  const handleConfirmAcceptance = async () => {
    if (confirmingAcceptance || !state) return;
    setConfirmingAcceptance(true);
    await api.setAcceptanceCriteria(true);
    await fetchData();
    setConfirmingAcceptance(false);
  };

  // Revise acceptance handler
  const handleReviseAcceptance = async () => {
    if (confirmingAcceptance || !state) return;
    setConfirmingAcceptance(true);
    await api.setAcceptanceCriteria(false);
    await fetchData();
    setConfirmingAcceptance(false);
  };

  // Prepare execution handler - advances to CONTROL_PLANE
  const handlePrepareExecution = async () => {
    if (preparingExecution || !state) return;
    setPreparingExecution(true);
    await api.prepareExecution();
    await fetchData();
    setPreparingExecution(false);
  };

  // Compute blocked reason
  const getBlockedReason = (): string | null => {
    if (!state) return 'Loading...';
    if (!orchestrationEnabled) return null; // Orchestration off = no blocking

    if (!state.has_frozen_intent) return 'No frozen intent';
    if (!state.has_acceptance_criteria) return 'No acceptance criteria';
    if (state.deviation_count > 0) return `${state.deviation_count} deviation(s) detected`;
    if (state.judgment_pending) return 'Awaiting judgment from GPT-5.2';
    if (state.judgment_verdict === 'FAIL') return 'Judgment failed';
    if (state.judgment_verdict === 'UNAVAILABLE') return 'Judgment unavailable';
    if (!state.execution_confirmed) return 'Awaiting confirmation';

    return null;
  };

  const blockedReason = getBlockedReason();
  const isUnlocked = !blockedReason && state?.execution_confirmed;
  const canConfirm = state &&
    state.has_frozen_intent &&
    state.has_acceptance_criteria &&
    state.deviation_count === 0 &&
    !state.execution_confirmed &&
    state.judgment_verdict !== 'FAIL' &&
    state.judgment_verdict !== 'UNAVAILABLE';

  // Loading state
  if (loading && !state) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900/50">
        <div className="text-center">
          <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto mb-2" />
          <p className="text-xs text-gray-500">Connecting...</p>
        </div>
      </div>
    );
  }

  // Not connected
  if (!connected) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4 bg-gray-900/50">
        <XCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-sm text-red-300 text-center mb-4">Orchestrator Offline</p>
        <button
          onClick={handleReset}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded text-sm text-gray-300"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-900/50 overflow-hidden">
      {/* Header - Orchestration Toggle */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-cyan-400" />
          <span className="text-sm font-semibold text-white">Orchestrator</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleToggleOrchestration}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              orchestrationEnabled
                ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                : 'bg-gray-700/50 text-gray-400 border border-gray-600/30'
            }`}
          >
            <Power className="w-3 h-3" />
            {orchestrationEnabled ? 'ON' : 'OFF'}
          </button>
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`} />
        </div>
      </div>

      {/* Mode Badges */}
      <div className="px-4 py-3 border-b border-gray-800 shrink-0">
        <div className="flex gap-1">
          {MODES.map((mode) => {
            const isActive = state?.mode === mode.key;
            return (
              <div
                key={mode.key}
                className={`flex-1 py-1.5 text-center text-[10px] font-medium rounded border transition-colors ${
                  getModeColorClasses(mode.key, isActive)
                }`}
              >
                {mode.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Execution Status - Centered */}
      <div className={`px-4 py-4 border-b shrink-0 ${
        isUnlocked
          ? 'bg-green-900/20 border-green-800/30'
          : 'bg-red-900/10 border-gray-800'
      }`}>
        <div className="flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2 mb-1">
            {isUnlocked ? (
              <Unlock className="w-5 h-5 text-green-400" />
            ) : (
              <Lock className="w-5 h-5 text-red-400" />
            )}
            <span className={`text-base font-bold ${isUnlocked ? 'text-green-400' : 'text-red-400'}`}>
              {isUnlocked ? 'EXECUTION UNLOCKED' : 'EXECUTION LOCKED'}
            </span>
          </div>
          {blockedReason && (
            <span className="text-sm text-red-300">{blockedReason}</span>
          )}
        </div>
      </div>

      {/* Control Buttons */}
      <div className="px-4 py-3 border-b border-gray-800 shrink-0">
        <div className="grid grid-cols-2 gap-2 mb-3">
          {/* Judgment Gate Toggle */}
          <button
            onClick={handleToggleJudgmentGate}
            disabled={judgmentGateLoading}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium transition-colors ${
              judgmentGateEnabled
                ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30 hover:bg-purple-600/30'
                : 'bg-gray-700/50 text-gray-400 border border-gray-600/30 hover:bg-gray-700'
            } disabled:opacity-50`}
          >
            {judgmentGateLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Zap className="w-3.5 h-3.5" />
            )}
            Judgment: {judgmentGateEnabled ? 'ON' : 'OFF'}
          </button>

          {/* Reset Button */}
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50"
          >
            {resetting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RotateCcw className="w-3.5 h-3.5" />
            )}
            Reset
          </button>
        </div>

        {/* Verdict Controls - Always visible for quick PASS access */}
        <div className={`mb-3 p-2 rounded border ${judgmentGateEnabled ? 'border-purple-700/30 bg-purple-900/10' : 'border-gray-700/30 bg-gray-800/20'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-[10px] font-medium ${judgmentGateEnabled ? 'text-purple-400' : 'text-gray-500'}`}>VERDICT CONTROL</span>
            <span className={`text-[10px] ${
              state?.judgment_verdict === 'PASS' ? 'text-green-400' :
              state?.judgment_verdict === 'FAIL' ? 'text-red-400' : 'text-yellow-400'
            }`}>
              {state?.judgment_verdict || 'PENDING'}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {JUDGMENT_VERDICTS.map((verdict) => (
              <button
                key={verdict}
                onClick={() => handleSetVerdict(verdict)}
                disabled={judgmentGateLoading || state?.judgment_verdict === verdict}
                className={`py-1.5 rounded text-[10px] font-medium transition-colors ${
                  state?.judgment_verdict === verdict
                    ? verdict === 'PASS' ? 'bg-green-600/30 text-green-300 border border-green-500/50'
                      : verdict === 'FAIL' ? 'bg-red-600/30 text-red-300 border border-red-500/50'
                      : 'bg-yellow-600/30 text-yellow-300 border border-yellow-500/50'
                    : 'bg-gray-700/50 text-gray-400 border border-gray-600/30 hover:bg-gray-700'
                } disabled:opacity-70`}
              >
                {verdict}
              </button>
            ))}
          </div>
        </div>

        {/* Prepare Execution Button */}
        <button
          onClick={handlePrepareExecution}
          disabled={preparingExecution || state?.phase === 'CONTROL_PLANE' || state?.phase === 'EXECUTION'}
          className={`w-full mb-3 py-2.5 rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            state?.phase === 'CONTROL_PLANE' || state?.phase === 'EXECUTION'
              ? 'bg-cyan-800/30 text-cyan-400 border border-cyan-700/30 cursor-default'
              : 'bg-cyan-600 hover:bg-cyan-500 text-white cursor-pointer disabled:opacity-50'
          } ${state?.phase !== 'CONTROL_PLANE' && state?.phase !== 'EXECUTION' && !preparingExecution ? 'orch-btn-glow' : ''}`}
        >
          {preparingExecution ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : state?.phase === 'CONTROL_PLANE' || state?.phase === 'EXECUTION' ? (
            <>
              <Check className="w-4 h-4" />
              Ready for Execution
            </>
          ) : (
            <>
              <Zap className="w-4 h-4" />
              Prepare Execution
            </>
          )}
        </button>

        {/* Proposed Intent - Confirm/Revise */}
        <div className="mb-3 p-2 rounded border border-gray-700/50 bg-gray-800/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-gray-400">PROPOSED INTENT</span>
            <span className={`text-[10px] ${state?.has_frozen_intent ? 'text-green-400' : 'text-gray-500'}`}>
              {state?.has_frozen_intent ? 'Confirmed' : 'Pending'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleConfirmIntent}
              disabled={state?.has_frozen_intent || confirmingIntent}
              className={`py-1.5 rounded text-[10px] font-medium transition-colors ${
                state?.has_frozen_intent
                  ? 'bg-green-800/30 text-green-400 border border-green-700/30'
                  : 'bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/30'
              } ${!state?.has_frozen_intent && !confirmingIntent ? 'orch-btn-glow' : ''}`}
            >
              {confirmingIntent ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : state?.has_frozen_intent ? '✓ Confirmed' : 'Confirm'}
            </button>
            <button
              onClick={handleReviseIntent}
              disabled={!state?.has_frozen_intent || confirmingIntent}
              className="py-1.5 rounded text-[10px] font-medium bg-gray-700/50 text-gray-400 border border-gray-600/30 hover:bg-gray-700 hover:text-gray-300 transition-colors disabled:opacity-50"
            >
              Revise
            </button>
          </div>
        </div>

        {/* Proposed Acceptance - Confirm/Revise */}
        <div className="mb-3 p-2 rounded border border-gray-700/50 bg-gray-800/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-gray-400">ACCEPTANCE CRITERIA</span>
            <span className={`text-[10px] ${state?.has_acceptance_criteria ? 'text-green-400' : 'text-gray-500'}`}>
              {state?.has_acceptance_criteria ? 'Confirmed' : 'Pending'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleConfirmAcceptance}
              disabled={state?.has_acceptance_criteria || confirmingAcceptance}
              className={`py-1.5 rounded text-[10px] font-medium transition-colors ${
                state?.has_acceptance_criteria
                  ? 'bg-green-800/30 text-green-400 border border-green-700/30'
                  : 'bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/30'
              } ${state?.has_frozen_intent && !state?.has_acceptance_criteria && !confirmingAcceptance ? 'orch-btn-glow' : ''}`}
            >
              {confirmingAcceptance ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : state?.has_acceptance_criteria ? '✓ Confirmed' : 'Confirm'}
            </button>
            <button
              onClick={handleReviseAcceptance}
              disabled={!state?.has_acceptance_criteria || confirmingAcceptance}
              className="py-1.5 rounded text-[10px] font-medium bg-gray-700/50 text-gray-400 border border-gray-600/30 hover:bg-gray-700 hover:text-gray-300 transition-colors disabled:opacity-50"
            >
              Revise
            </button>
          </div>
        </div>

        {/* Confirm Execution Button */}
        <button
          onClick={handleConfirmExecution}
          disabled={!canConfirm || confirming}
          className={`w-full py-2.5 rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            isUnlocked
              ? 'bg-green-800/30 text-green-400 cursor-default border border-green-700/30'
              : canConfirm
              ? 'bg-green-600 hover:bg-green-500 text-white cursor-pointer'
              : 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700/30'
          } ${canConfirm && !confirming ? 'orch-btn-ready' : ''}`}
        >
          {confirming ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isUnlocked ? (
            <>
              <Check className="w-4 h-4" />
              Execution Confirmed
            </>
          ) : (
            'Confirm Execution'
          )}
        </button>
      </div>

      {/* Status Indicators */}
      <div className="px-4 py-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-1.5 ${state?.warning_count ? 'text-yellow-400' : 'text-gray-500'}`}>
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Warnings: {state?.warning_count || 0}</span>
            </div>
            <div className={`flex items-center gap-1.5 ${state?.deviation_count ? 'text-red-400' : 'text-gray-500'}`}>
              <XCircle className="w-3.5 h-3.5" />
              <span>Deviations: {state?.deviation_count || 0}</span>
            </div>
          </div>
          {state?.judgment_verdict && state.judgment_verdict !== 'PENDING' && (
            <div className={`flex items-center gap-1.5 ${
              state.judgment_verdict === 'PASS' ? 'text-green-400' : 'text-red-400'
            }`}>
              {state.judgment_verdict === 'PASS' ? (
                <CheckCircle className="w-3.5 h-3.5" />
              ) : (
                <XCircle className="w-3.5 h-3.5" />
              )}
              <span>Judgment: {state.judgment_verdict}</span>
            </div>
          )}
        </div>
      </div>

      {/* PZRP Scopes */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="px-4 py-2 flex items-center gap-2 border-b border-gray-800/50 shrink-0 bg-cyan-900/10">
          <Settings2 className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium text-cyan-300">PZRP Scopes</span>
          <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
            pzrpScopes.filter(s => s.active).length > 0
              ? 'bg-green-900/30 text-green-400'
              : 'bg-red-900/30 text-red-400'
          }`}>
            {pzrpScopes.filter(s => s.active).length}/{pzrpScopes.length} ACTIVE
          </span>
        </div>

        {/* Quick Actions */}
        <div className="px-4 py-2 border-b border-gray-800/30 shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={handleGrantAll}
              disabled={scopesLoading}
              className="flex-1 py-1.5 rounded text-xs font-medium bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30 transition-colors disabled:opacity-50"
            >
              Grant All
            </button>
            <button
              onClick={handleClearScopes}
              disabled={scopesLoading}
              className="flex-1 py-1.5 rounded text-xs font-medium bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30 transition-colors disabled:opacity-50"
            >
              Revoke All
            </button>
          </div>
        </div>

        {/* Scope Toggles */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="space-y-2">
            {pzrpScopes.map((scope) => {
              const ScopeIcon = scope.name === 'READ_PZRP' ? Eye :
                scope.name === 'EDIT_PZRP' ? Edit3 :
                scope.name === 'TEST_PZRP' ? TestTube :
                scope.name === 'BUILD_PZRP' ? Hammer :
                scope.name === 'EXPLORE_PZRP' ? Compass :
                Settings2;

              return (
                <button
                  key={scope.name}
                  onClick={() => handleToggleScope(scope.name, !scope.active)}
                  disabled={scopesLoading}
                  className={`w-full p-3 rounded-lg border text-left transition-all ${
                    scope.active
                      ? 'bg-cyan-900/20 border-cyan-500/40 hover:bg-cyan-900/30'
                      : 'bg-gray-800/30 border-gray-700/30 hover:bg-gray-800/50'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded ${
                      scope.active ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-700/50 text-gray-500'
                    }`}>
                      <ScopeIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${scope.active ? 'text-cyan-300' : 'text-gray-400'}`}>
                          {scope.name}
                        </span>
                        {scope.active && (
                          <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                        )}
                      </div>
                      <p className="text-[10px] text-gray-500 truncate mt-0.5">
                        {scope.description}
                      </p>
                    </div>
                  </div>
                  {scope.dependencies.length > 0 && (
                    <div className="mt-2 text-[10px] text-gray-600">
                      Requires: {scope.dependencies.join(', ')}
                    </div>
                  )}
                </button>
              );
            })}

            {pzrpScopes.length === 0 && (
              <div className="text-center py-8 text-gray-500 text-sm">
                <Settings2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Loading scopes...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer - Session Info */}
      <div className="px-4 py-2 border-t border-gray-800 shrink-0 bg-gray-900/30">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Session: {state?.session_id?.slice(0, 8)}...</span>
          <span>Mode: {state?.mode || 'NORMAL'}</span>
        </div>
      </div>
    </div>
  );
};

export default OrchestratorPanel;
